// components/user/profile/ProfileEditForm.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Input, Card } from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useFormik } from "formik";
import {
  updateProfile,
  clearProfileError,
} from "../../../store/slices/profileSlice";
import { profileValidationSchema } from "../../../validations/profileValidation";
import toast from "react-hot-toast";

const ProfileEditForm = ({ onCancel, isDarkMode }) => {
  const dispatch = useDispatch();
  const { user, updatingProfile, error } = useSelector(
    (state) => state.profile,
  );

  const cleanValue = (value) => {
    if (!value || value === "null" || value === "undefined") return "";
    return value;
  };

  const formik = useFormik({
    initialValues: {
      name: cleanValue(user?.name),
      email: cleanValue(user?.email),
      mobile: cleanValue(user?.mobile),
      pincode: cleanValue(user?.pincode),
      address: cleanValue(user?.address),
    },
    validationSchema: profileValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result = await dispatch(updateProfile(values));
      if (updateProfile.fulfilled.match(result)) {
        toast.success("Profile updated successfully");
        onCancel();
      } else if (updateProfile.rejected.match(result)) {
        toast.error(result.payload || "Failed to update profile");
      }
    },
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearProfileError());
    }
  }, [error, dispatch]);

  return (
    <Card className="border-0 shadow-sm rounded-xl">
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-gray-100 dark:border-gray-700">
        <h3
          className={`text-sm font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
        >
          Edit Profile Information
        </h3>
      </div>

      <form onSubmit={formik.handleSubmit} className="pt-2">
        <div className="mb-4">
          <label
            className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Full Name *
          </label>
          <Input
            name="name"
            placeholder="Enter your full name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<UserOutlined className="text-xs text-gray-400" />}
            className={`text-sm rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            size="middle"
            status={formik.touched.name && formik.errors.name ? "error" : ""}
          />
          {formik.touched.name && formik.errors.name && (
            <div className="mt-1 text-xs text-red-500">
              {formik.errors.name}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label
            className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Email Address *
          </label>
          <Input
            name="email"
            type="email"
            placeholder="Enter your email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            prefix={<MailOutlined className="text-xs text-gray-400" />}
            className={`text-sm rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            size="middle"
            status={formik.touched.email && formik.errors.email ? "error" : ""}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="mt-1 text-xs text-red-500">
              {formik.errors.email}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label
            className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Mobile Number *
          </label>
          <Input
            name="mobile"
            placeholder="Enter mobile number"
            value={formik.values.mobile}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 10);
              formik.setFieldValue("mobile", value);
            }}
            onBlur={formik.handleBlur}
            prefix={<PhoneOutlined className="text-xs text-gray-400" />}
            className={`text-sm rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            size="middle"
            maxLength={10}
            status={
              formik.touched.mobile && formik.errors.mobile ? "error" : ""
            }
          />
          {formik.touched.mobile && formik.errors.mobile && (
            <div className="mt-1 text-xs text-red-500">
              {formik.errors.mobile}
            </div>
          )}
        </div>

        <div className="mb-4">
          <label
            className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Pincode *
          </label>
          <Input
            name="pincode"
            placeholder="Enter pincode"
            value={formik.values.pincode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 6);
              formik.setFieldValue("pincode", value);
            }}
            onBlur={formik.handleBlur}
            prefix={<EnvironmentOutlined className="text-xs text-gray-400" />}
            className={`text-sm rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            size="middle"
            maxLength={6}
            status={
              formik.touched.pincode && formik.errors.pincode ? "error" : ""
            }
          />
          {formik.touched.pincode && formik.errors.pincode && (
            <div className="mt-1 text-xs text-red-500">
              {formik.errors.pincode}
            </div>
          )}
        </div>

        <div className="mb-6">
          <label
            className={`block text-xs font-medium mb-1.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Address *
          </label>
          <Input.TextArea
            name="address"
            rows={3}
            placeholder="Enter your complete address"
            value={formik.values.address}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`text-sm rounded-lg ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            status={
              formik.touched.address && formik.errors.address ? "error" : ""
            }
          />
          {formik.touched.address && formik.errors.address && (
            <div className="mt-1 text-xs text-red-500">
              {formik.errors.address}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onCancel}
            icon={<CloseOutlined />}
            size="middle"
            className="flex-1"
            disabled={updatingProfile}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updatingProfile}
            icon={<SaveOutlined />}
            size="middle"
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ProfileEditForm;
